import React, { useState } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, IconButton, Input, Option, Select
} from "@material-tailwind/react";
import { PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import {toLocalDateTimeInputValue} from "../../../utils/dateTime";

export default function AddItemDialog({ isOpen, onClose, onSuccess, requestHeaders }) {

    const [newItemData, setNewItemData] = useState({
        title: '',
        author: '',
        itemCondition: '',
        formatType: '',
        price: 0,
        priceFees: 0,
        shippingFees: 0,
        parcelForwardingFees: 0,
        expectedSellingPriceInRsd: '',
        wobOrderNumber: 'FREE',
        status: '',
        purchasedDate: toLocalDateTimeInputValue(),
        imageUrl: '',
        type: '',
        paymentType: null
    });

    const resetForm = () => {
        setNewItemData({
            title: '', author: '', itemCondition: '', formatType: '',
            price: 0, priceFees: 0, shippingFees: 0, parcelForwardingFees: 0,
            expectedSellingPriceInRsd: '', wobOrderNumber: 'FREE', status: '',
            purchasedDate: toLocalDateTimeInputValue(), imageUrl: '', type: '', paymentType: null
        });
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setNewItemData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        try {
            await axios.post(`${API_URL}/purchased_items/add`, newItemData, { headers: requestHeaders });
            resetForm();
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Error adding item:", err);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Kad se dialogs otvori, postavi purchasedDate na trenutni timestamp
    React.useEffect(() => {
        if (isOpen) {
            setNewItemData(prev => ({ ...prev, purchasedDate: toLocalDateTimeInputValue(new Date(), true) }));
        }
    }, [isOpen]);

    return (
        <Dialog size="l" open={isOpen} handler={handleClose}
                className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden dark:bg-dark-900 dark:border-1 dark:border-gray-800">
            <DialogHeader className="relative m-0 block">
                <Typography variant="h4" color="blue-gray" className="dark:text-gray-300">Add new item</Typography>
                <Typography className="mt-1 font-normal text-gray-600">Import item you bought.</Typography>
                <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={handleClose}>
                    <XMarkIcon className="h-4 w-4 stroke-2 dark:text-gray-600 dark:hover:text-gray-500" />
                </IconButton>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Name</Typography>
                        <Input color="gray" size="lg" placeholder="eg. Naruto" name="title" value={newItemData.title} onChange={handleInput}
                            className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Author or Platform</Typography>
                        <Input color="gray" size="lg" placeholder="eg. Akira Toriyama or PS4" name="author" value={newItemData.author} onChange={handleInput}
                            className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Condition</Typography>
                            <Input color="gray" size="lg" placeholder="eg. Very Good" name="itemCondition" value={newItemData.itemCondition} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Format type</Typography>
                            <Input color="gray" size="lg" placeholder="eg. Paperback" name="formatType" value={newItemData.formatType} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Price (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="price" value={newItemData.price} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Price Fees (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="priceFees" value={newItemData.priceFees} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Shipping Fees (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="shippingFees" value={newItemData.shippingFees} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Parcel Forwarding Fees (£)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="parcelForwardingFees" value={newItemData.parcelForwardingFees} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Expected Selling Price (RSD)</Typography>
                            <Input type="number" step="0.01" color="gray" size="lg" placeholder="eg. 100.00" name="expectedSellingPriceInRsd" value={newItemData.expectedSellingPriceInRsd} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">WOB Order Number</Typography>
                            <Input color="gray" size="lg" placeholder="eg. WOB123456789GB" name="wobOrderNumber" value={newItemData.wobOrderNumber} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Status</Typography>
                            <Select name="status"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white dark:bg-dark-850 text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'status', value } })}
                                value={newItemData.status}>
                                <Option value="DISPATCHED">Dispatched</Option>
                                <Option value="IN_STOCK">In stock</Option>
                                <Option value="SOLD">Sold</Option>
                                <Option value="CANCELLED">Cancelled</Option>
                                <Option value="GIVEAWAY">Giveaway</Option>
                                <Option value="IN_STOCK_PERSONAL">In Stock Personal</Option>
                                <Option value="DISPATCHED_PERSONAL">Dispatched Personal</Option>
                                <Option value="DAMAGED">Damaged</Option>
                            </Select>
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Purchased date</Typography>
                            <Input type="datetime-local" color="gray" size="lg" placeholder="Select date and time" name="purchasedDate" value={newItemData.purchasedDate} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Image URL</Typography>
                            <Input color="gray" size="lg" placeholder="eg. https://..." name="imageUrl" value={newItemData.imageUrl} onChange={handleInput}
                                className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850" containerProps={{ className: "!min-w-full" }} labelProps={{ className: "hidden" }} />
                        </div>
                        <div className="w-full">
                            <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">Type</Typography>
                            <Select name="type"
                                className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white dark:bg-dark-850 text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                labelProps={{ className: "hidden" }}
                                onChange={(value) => handleInput({ target: { name: 'type', value } })}
                                value={newItemData.type}>
                                <Option value="BOOK">Book</Option>
                                <Option value="GAME">Game</Option>
                            </Select>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="flex-shrink-0">
                    <Button type="submit" className="mv-primary-button ml-auto">
                        <PlusCircleIcon className="h-4 w-4" />
                        Add Item
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}
